import { create } from 'zustand';
import type { Place } from '@wingman/shared';

export type TimelineMode = 'discovery' | 'route' | 'timeofday';

export interface LayerVisibility {
  routes: boolean;
  facilities: boolean;
  traffic: boolean;
  weather: boolean;
  heatmap: boolean;
  events: boolean;
}

export interface BriefingData {
  totalCount: number;
  openCount: number;
  closingSoonCount: number;
  topRated: { id: string; name: string; rating: number; categoryName: string }[];
  categoryBreakdown: { slug: string; name: string; count: number; color: string }[];
  watchFor: string[];
}

export interface SessionStats {
  detected: number;
  openNow: number;
  filtered: number;
  viewed: number;
  routesPlanned: number;
}

interface CommandState {
  timelineMode: TimelineMode;
  timelineCursor: number;
  timelinePlaying: boolean;
  timelineSpeed: number;
  layerVisibility: LayerVisibility;
  focusDayId: string;
  briefingData: BriefingData | null;
  sessionStats: SessionStats;
  highlightedPlaceId: string | null;
  vehicleProgress: number;
  setTimelineMode: (mode: TimelineMode) => void;
  setTimelineCursor: (cursor: number) => void;
  setTimelinePlaying: (playing: boolean) => void;
  setTimelineSpeed: (speed: number) => void;
  toggleLayer: (layer: keyof LayerVisibility) => void;
  setFocusDay: (dayId: string) => void;
  setBriefingData: (data: BriefingData | null) => void;
  setSessionStats: (stats: Partial<SessionStats>) => void;
  setHighlightedPlaceId: (id: string | null) => void;
  setVehicleProgress: (progress: number) => void;
}

export const useCommandStore = create<CommandState>((set) => ({
  timelineMode: 'discovery',
  timelineCursor: 0,
  timelinePlaying: false,
  timelineSpeed: 1,
  layerVisibility: {
    routes: true,
    facilities: true,
    traffic: false,
    weather: false,
    heatmap: false,
    events: true,
  },
  focusDayId: 'all',
  briefingData: null,
  sessionStats: { detected: 0, openNow: 0, filtered: 0, viewed: 0, routesPlanned: 0 },
  highlightedPlaceId: null,
  vehicleProgress: 0,

  setTimelineMode: (mode) => set({ timelineMode: mode, timelineCursor: 0, timelinePlaying: false }),
  setTimelineCursor: (cursor) => set({ timelineCursor: Math.max(0, Math.min(1, cursor)) }),
  setTimelinePlaying: (playing) => set({ timelinePlaying: playing }),
  setTimelineSpeed: (speed) => set({ timelineSpeed: speed }),
  toggleLayer: (layer) => set((state) => ({
    layerVisibility: { ...state.layerVisibility, [layer]: !state.layerVisibility[layer] },
  })),
  setFocusDay: (dayId) => set({ focusDayId: dayId }),
  setBriefingData: (data) => set({ briefingData: data }),
  setSessionStats: (stats) => set((state) => ({ sessionStats: { ...state.sessionStats, ...stats } })),
  setHighlightedPlaceId: (id) => set({ highlightedPlaceId: id }),
  setVehicleProgress: (progress) => set({ vehicleProgress: Math.max(0, Math.min(1, progress)) }),
}));
